"use client"

import { useEffect, useRef, useState } from "react"
import { Icon } from "@/shared/ui/icon"

type LauncherProps = {
  teasers: string[]
  onOpen: () => void
  isLoading?: boolean
}

const TEASER_TS_KEY = "webdad-ai-teaser-ts"
const SHOW_DELAY = 3500
// Don't show another teaser more often than this (e.g. across page navigations).
const MIN_INTERVAL = 90_000

/**
 * Floating action button with a soft teaser bubble that invites the visitor to
 * start chatting. A teaser appears shortly after landing on a page, throttled
 * so it never nags.
 */
export function Launcher({ teasers, onOpen, isLoading = false }: LauncherProps) {
  const [teaser, setTeaser] = useState<string | null>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  // Re-arm when the page's teaser pool changes (navigation).
  const poolKey = teasers[0] ?? ""

  useEffect(() => {
    if (typeof window === "undefined") return

    let last = 0
    try {
      last = Number(window.sessionStorage.getItem(TEASER_TS_KEY) ?? 0)
    } catch {
      /* Storage can be disabled. */
    }
    if (Date.now() - last < MIN_INTERVAL) return

    const timer = window.setTimeout(() => {
      setTeaser(teasers[Math.floor(Math.random() * teasers.length)])
      try {
        window.sessionStorage.setItem(TEASER_TS_KEY, String(Date.now()))
      } catch {
        /* ignore */
      }
    }, SHOW_DELAY)

    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolKey])

  return (
    <div key="launcher" className="relative w-fit">
      {/* Keep the fixed launcher's dimensions stable when the teaser appears. */}
      {teaser ? (
        <div className="absolute bottom-full right-0 mb-3 w-60 max-w-[calc(100vw-2.5rem)] motion-safe:transition-[opacity,transform] motion-safe:duration-200 motion-safe:ease-out motion-safe:starting:translate-y-2 motion-safe:starting:opacity-0">
          <button
            type="button"
            onClick={onOpen}
            disabled={isLoading}
            className="w-full rounded-2xl rounded-br-md border border-slate-200 bg-white px-3.5 py-2.5 text-left text-sm text-slate-700 shadow-xl transition-shadow hover:shadow-2xl dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-100"
          >
            {teaser}
          </button>
          <button
            type="button"
            onClick={() => {
              setTeaser(null)
              launcherRef.current?.focus({ preventScroll: true })
            }}
            className="absolute -right-1.5 -top-1.5 inline-flex size-5 cursor-pointer items-center justify-center rounded-full bg-slate-200 text-slate-500 transition hover:bg-slate-300 dark:bg-zinc-700 dark:text-zinc-300"
            aria-label="Скрыть подсказку"
          >
            <Icon name="close-circle-linear" className="size-3.5" />
          </button>
        </div>
      ) : null}

      <button
        ref={launcherRef}
        type="button"
        onClick={onOpen}
        className="group relative flex h-14 items-center gap-2.5 rounded-full bg-gradient-to-r from-brand to-brand-600 pl-2 pr-4 text-white shadow-xl transition hover:shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 dark:from-jungle-500 dark:to-jungle-700"
        disabled={isLoading}
        aria-busy={isLoading}
        aria-label={isLoading ? "Загрузка AI-консультанта" : "Открыть AI-консультанта"}
      >
        <span className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-brand/40 motion-safe:animate-ping [animation-duration:2.6s] [animation-iteration-count:3] dark:ring-jungle-400/40" />
        <span className="relative inline-flex size-10 items-center justify-center rounded-full bg-white text-brand-600 dark:text-jungle-600">
          <Icon name="chat-round-dots-bold-duotone" className="size-6" />
        </span>
        <span className="relative hidden text-sm font-semibold sm:inline">
          {isLoading ? "Загружаем…" : "Спросить AI"}
        </span>
      </button>
    </div>
  )
}
